import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiFetch, readJsonResponse } from '../api/client';
import { withScheduleGaps } from './chartScheduleGaps';

const PERIOD_OPTIONS = [
  { value: 'all', label: 'Вся жизнь' },
  { value: '1y', label: '1 год' },
  { value: '90d', label: '90 дней' },
  { value: '30d', label: '30 дней' },
];

function formatDateTime(value) {
  if (!value) {
    return '—';
  }
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatAxisDate(value, period) {
  if (!value) {
    return '';
  }
  const options =
    period === '30d'
      ? { day: '2-digit', month: '2-digit' }
      : { month: 'short', year: '2-digit' };
  return new Intl.DateTimeFormat('ru-RU', options).format(new Date(value));
}

function SignalTooltip({ active, payload }) {
  if (!active || !payload?.[0]?.payload) {
    return null;
  }

  const point = payload[0].payload;
  return (
    <div className="max-w-[15rem] rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-xl dark:border-slate-700 dark:bg-slate-950">
      <p className="font-semibold text-slate-900 dark:text-white">
        Минимум: {point.minimum_signal_dbm} дБм
      </p>
      <p className="mt-1 text-slate-500">Зафиксирован: {formatDateTime(point.minimum_at)}</p>
      <p className="text-slate-500">Минимум повторился: {point.minimum_count || 0}</p>
      <p className="text-slate-500">Замеров в интервале: {point.sample_count}</p>
    </div>
  );
}

function SignalDot(props) {
  const { cx, cy, payload } = props;
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
    return null;
  }
  const color = signalColor(payload.minimum_signal_dbm);
  return (
    <circle
      cx={cx}
      cy={cy}
      r={3}
      fill={color}
      stroke="white"
      strokeWidth={1}
    />
  );
}

/** A hole only when the next sample is later than this device's link interval. */
export function buildSignalChartData(points, _bucketSeconds, periodSeconds) {
  return withScheduleGaps(points, periodSeconds, { minimum_signal_dbm: null });
}

/** Paint each minimum until the next sample. A real hole stays uncolored. */
export function buildSignalBands(chartData, bucketSeconds) {
  const bucketMs = Math.max(1, Number(bucketSeconds) || 1) * 1000;
  const bands = [];

  for (let index = 0; index < (chartData || []).length; index += 1) {
    const point = chartData[index];
    if (point.is_gap || point.minimum_signal_dbm == null || !Number.isFinite(point.bucket_ms)) {
      continue;
    }

    const following = chartData[index + 1];
    const reachesNext =
      following && !following.is_gap && Number.isFinite(following.bucket_ms) && following.bucket_ms > point.bucket_ms;
    bands.push({
      ...point,
      x1: point.bucket_ms,
      x2: reachesNext ? following.bucket_ms : point.bucket_ms + bucketMs,
    });
  }

  return bands;
}

/** Continuous rainbow: weaker signal is redder, stronger signal is greener. */
export function signalColor(signalDbm) {
  const signal = Number(signalDbm);
  if (!Number.isFinite(signal)) {
    return 'hsl(0 0% 60%)';
  }
  const normalized = Math.min(1, Math.max(0, (signal + 120) / 70));
  return `hsl(${Math.round(normalized * 120)} 75% 42%)`;
}

/** Build one step gradient so adjacent buckets cannot leave sub-pixel seams. */
export function buildSignalGradient(bands) {
  const validBands = (bands || [])
    .filter((band) => Number.isFinite(band.x1) && Number.isFinite(band.x2) && band.x2 > band.x1)
    .sort((left, right) => left.x1 - right.x1);
  if (!validBands.length) {
    return null;
  }

  const x1 = validBands[0].x1;
  const x2 = validBands.reduce((maximum, band) => Math.max(maximum, band.x2), x1);
  const span = Math.max(1, x2 - x1);
  const stops = [];
  let previousEnd = x1;

  validBands.forEach((band, index) => {
    const start = Math.min(1, Math.max(0, (band.x1 - x1) / span));
    const end = Math.min(1, Math.max(0, (band.x2 - x1) / span));
    const color = signalColor(band.minimum_signal_dbm);
    if (band.x1 > previousEnd) {
      const holeStart = Math.min(1, Math.max(0, (previousEnd - x1) / span));
      const holeEnd = Math.min(1, Math.max(0, (band.x1 - x1) / span));
      stops.push({ key: `${index}-hole-start`, offset: holeStart, color: 'transparent' });
      stops.push({ key: `${index}-hole-end`, offset: holeEnd, color: 'transparent' });
    }

    stops.push({ key: `${index}-start`, offset: start, color });
    stops.push({ key: `${index}-end`, offset: end, color });
    previousEnd = Math.max(previousEnd, band.x2);
  });

  return { x1, x2, stops };
}

/** Lifetime signal minima. Every bucket retains its exact minimum and occurrence count. */
export default function DeviceSignalHistory({ deviceId, onLoadingChange }) {
  const gradientId = `signal-gradient-${useId().replaceAll(':', '')}`;
  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!deviceId) {
      setIsLoading(false);
      onLoadingChangeRef.current?.(false);
      return undefined;
    }

    const abortController = new AbortController();
    async function load() {
      setIsLoading(true);
      onLoadingChangeRef.current?.(true);
      setError(null);
      try {
        const response = await apiFetch(
          `/api/devices/${deviceId}/signal-history?period=${encodeURIComponent(period)}`,
          { signal: abortController.signal },
        );
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.message || `Ошибка ${response.status}`);
        }
        const payload = await readJsonResponse(response, 'История сигнала');
        if (!abortController.signal.aborted) {
          setData(payload);
        }
      } catch (loadError) {
        if (abortController.signal.aborted || loadError?.name === 'AbortError') {
          return;
        }
        setData(null);
        setError(loadError.message || 'Не удалось загрузить сигнал');
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
          onLoadingChangeRef.current?.(false);
        }
      }
    }

    load();
    return () => abortController.abort();
  }, [deviceId, period]);

  const points = data?.points || [];
  const chartData = useMemo(
    () => buildSignalChartData(data?.points || [], data?.bucket_seconds, data?.connection_period_seconds),
    [data?.bucket_seconds, data?.connection_period_seconds, data?.points],
  );
  const signalBands = useMemo(
    () => buildSignalBands(chartData, data?.bucket_seconds),
    [chartData, data?.bucket_seconds],
  );
  const signalGradient = useMemo(() => buildSignalGradient(signalBands), [signalBands]);

  return (
    <section className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/30">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500">Сигнал</p>
          <h2 className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-white">
            Минимумы сигнала за жизнь устройства
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Каждый цветной интервал хранит точный минимум: краснее — хуже, зеленее — лучше
          </p>
        </div>
        <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800/80">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setPeriod(option.value)}
              className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
                period === option.value
                  ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-950 dark:text-white'
                  : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && !data ? (
        <div className="mt-3 h-56 animate-pulse rounded-lg bg-slate-200/70 dark:bg-slate-800" />
      ) : error ? (
        <p className="mt-3 rounded-lg border border-amber-300/40 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
          {error}
        </p>
      ) : points.length === 0 ? (
        <p className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-8 text-center text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-950/40">
          Устройство ещё не передавало корректный уровень сигнала
        </p>
      ) : (
        <>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/50">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Худший минимум</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                {data.worst_signal_dbm} дБм
              </p>
              <p className="text-[11px] text-slate-500">
                {data.worst_signal_count} раз · {formatDateTime(data.worst_at)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/50">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Интервал графика</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                {data.bucket_label || '—'}
              </p>
              <p className="text-[11px] text-slate-500">
                Дырка, если пропуск дольше интервала связи: {data.connection_period_label || 'день'}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/50">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Учтено замеров</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                {data.sample_count}
              </p>
              <p className="text-[11px] text-slate-500">Ни один минимум не отброшен</p>
            </div>
          </div>

          <div className={`mt-3 h-72 transition-opacity ${isLoading ? 'opacity-55' : 'opacity-100'}`}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="bucket_ms"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  minTickGap={32}
                  tick={{ fontSize: 10 }}
                  stroke="#94a3b8"
                  tickFormatter={(value) => formatAxisDate(value, period)}
                />
                <YAxis
                  type="number"
                  domain={[
                    (minimum) => Math.floor((minimum - 5) / 10) * 10,
                    (maximum) => Math.min(0, Math.ceil((maximum + 5) / 10) * 10),
                  ]}
                  tick={{ fontSize: 10 }}
                  stroke="#94a3b8"
                  tickFormatter={(value) => `${value}`}
                  width={38}
                  allowDataOverflow={false}
                />
                <Tooltip content={<SignalTooltip />} />
                {signalGradient ? (
                  <>
                    <defs>
                      <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                        {signalGradient.stops.map((stop) => (
                          <stop
                            key={stop.key}
                            offset={stop.offset}
                            stopColor={stop.color}
                            stopOpacity={stop.color === 'transparent' ? 0 : 1}
                          />
                        ))}
                      </linearGradient>
                    </defs>
                    <ReferenceArea
                      x1={signalGradient.x1}
                      x2={signalGradient.x2}
                      y1={-140}
                      y2={0}
                      fill={`url(#${gradientId})`}
                      fillOpacity={0.16}
                      stroke="none"
                      ifOverflow="hidden"
                    />
                  </>
                ) : null}
                <Line
                  type="linear"
                  dataKey="minimum_signal_dbm"
                  name="Минимум"
                  stroke="#475569"
                  strokeWidth={1.75}
                  connectNulls={false}
                  isAnimationActive={false}
                  dot={<SignalDot />}
                  activeDot={{ r: 5, fill: '#475569', stroke: '#fff', strokeWidth: 2 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-[10px] text-slate-400">хуже</span>
            <span
              className="h-1.5 flex-1 rounded-full"
              style={{ background: 'linear-gradient(90deg, hsl(0 75% 42%), hsl(60 75% 42%), hsl(120 75% 42%))' }}
            />
            <span className="text-[10px] text-slate-400">лучше</span>
          </div>
          <p className="mt-1 text-[10px] text-slate-400">
            Цвет рассчитан непрерывно по дБм, без порогов. Значения 99 и 0 не считаются сигналом.
          </p>
        </>
      )}
    </section>
  );
}
